<template>
  <div class="account-container" :class="{ 'mobile-style': containerWidth <= 1100 }">
    <div v-if="calIsFailAccount" class="fail-mask" />
    <div class="account-header">
      <AccountIcon
        :customClasses="calFailedClass"
        :accountStage="accountData.accountStage"
        :accountStatus="accountData.status"
        :accountNumber="accountData.accountNumber"
      />
      <div v-if="calNewAccount" class="new-tag">{{ $t('propTrade.tags.new') }}</div>
    </div>

    <div class="account-content">
      <div class="body-main">
        <img
          :style="{ height: '24px', width: '24px' }"
          :src="require(`@/assets/images/flag/currency/${accountData.currency}.png`)"
          :alt="accountData.currency"
        />
        <div class="equity-area">
          <EquityNumber :value="accountData.equity" />
          <div class="currency-text">{{ accountData.currency }}</div>
          <div
            v-if="accountData.profitRatio !== null"
            class="equity-text"
            :class="{ minus: accountData.profitRatio < 0 }"
          >
            {{ accountData.profitRatio < 0 ? '' : '+' }}{{ accountData.profitRatio.toFixed(2) }}%
          </div>
        </div>
      </div>
      <div class="body-bottom">
        <div class="body-bottom-items-box">
          <div v-if="accountData.platform" class="body-bottom-item">
            {{ accountData.platform }}
          </div>
          <div class="body-bottom-item" v-if="accountData.server">
            <div class="account-footer-detail-item">
              {{ accountData.server }}
            </div>
          </div>
          <div class="body-bottom-item" v-if="isStatusDescVisible">
            <div class="account-footer-detail-item">{{ statusDesc }} {{ accountData.statusDate }}</div>
          </div>
        </div>
        <div class="body-bottom-item" v-if="accountData.status !== null">
          <AccountStatus :accountStatus="accountData.status" />
        </div>
        <div v-if="isAuthVisible" class="body-bottom-item auth">
          <div class="auth-action" @click.prevent="$router.push({ name: 'register' })">
            <span>{{ $t('propTrade.dashboard.goToAuthentication') }}</span>
          </div>
          <el-popover trigger="hover" width="300" placement="bottom-end" class="info-icon">
            <i slot="reference" class="el-icon-warning"></i>
            <div>{{ $t('propTrade.dashboard.completeAuthentication') }}</div>
          </el-popover>
        </div>
      </div>

      <div class="action-area">
        <div
          v-if="accountData.status !== ACCOUNT_STATUS.PROCESSING"
          class="metrics-btn"
          data-testid="viewMetrics"
          @click="showAccountMetrics"
        >
          {{ $t('propTrade.button.viewMetrics') }}
        </div>

        <el-dropdown @command="handleCommand" trigger="click">
          <el-badge :hidden="false" class="item">
            <div class="more-button-wrapper">
              <img
                :src="accountSettingIcon"
                @mouseover="accountSettingIcon = settingIcons.hoverIcon"
                @mouseleave="accountSettingIcon = settingIcons.defaultIcon"
                class="more-button"
              />
              <div v-if="hasPayout" class="menu-dot"></div>
            </div>
          </el-badge>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item v-for="(menu, index) in dropdownList" :key="index" :command="menu.value">
              <div class="menu-item" @mouseover="hoverdSettingItem = menu.value" @mouseleave="hoverdSettingItem = null">
                <img
                  :src="hoverdSettingItem === menu.value ? menu.hoverIcon : menu.icon"
                  alt=""
                  class="menu_custom_icon"
                />
                {{ menu.title }}
                <div v-if="menu.value === 'payout'" class="menu-dot"></div>
              </div>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>

    <!-- history dialog -->
    <el-dialog :visible.sync="showHistory" class="history-dialog" :class="{ ar: ['ar', 'fa'].includes(lang)}" top="0">
      <template #title>
        <div class="history-content">
          <div class="history-content-main">{{ $t('propTrade.history.title') }}</div>
          <div class="history-content-desc">
            {{ $t('propTrade.history.sessionNumber') }}:
            <span>{{ accountData.sessionNumber }}</span>
          </div>
        </div>
      </template>
      <el-table :data="historyData" height="463" stripe>
        <el-table-column
          property="date"
          :label="$t('propTrade.history.field.date')"
          :width="drawerColumnWidth"
        ></el-table-column>
        <el-table-column property="event" :label="$t('propTrade.history.field.event')">
          <template slot-scope="scope">
            {{ scope.row.event }}
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <!-- reset password dialog -->
    <el-dialog
      :visible.sync="showRestPassword"
      width="640px"
      class="reset-pwd-dialog"
      :class="{ ar: ['ar', 'fa'].includes(lang) }"
      :before-close="cancelChangePassword"
      :close-on-click-modal="false"
      top="0"
    >
      <template #title>
        <div :class="['title-text', ['ar', 'fa'].includes(lang) ? 'ar' : '']">{{ $t('propTrading.button.changePw') }}</div>
        <div class="account-number-text">
          <div>{{ $t('propTrade.resetPassword.accountNumber') }}</div>
          <div>{{ accountData.accountNumber }}</div>
        </div>
      </template>
      <el-form :model="resetPwdForm" :rules="resetFormRules" ref="resetPwdFormRef">
        <el-form-item
          prop="currentPwd"
          class="reset-form-item"
          ref="currentPwd"
          :label="$t('propTrade.resetPassword.currentPwd')"
        >
          <el-input
            type="password"
            isPassword
            autocomplete="off"
            :placeholder="$t('propTrade.resetPassword.currentPwdPlaceholder')"
            v-model="resetPwdForm.currentPwd"
            dataTestId="currentPwd"
          />
        </el-form-item>

        <el-form-item prop="newPwd" class="reset-form-item" :label="$t('propTrade.resetPassword.newPwd')">
          <el-input
            type="password"
            isPassword
            autocomplete="off"
            :placeholder="$t('propTrade.resetPassword.newPwdPlaceholder')"
            v-model="resetPwdForm.newPwd"
            dataTestId="newPwd"
          />
        </el-form-item>

        <el-form-item
          prop="confirmNewPwd"
          class="reset-form-item"
          ref="confirmNewPwd"
          :label="$t('propTrade.resetPassword.confirmNewPwd')"
        >
          <el-input
            type="password"
            isPassword
            autocomplete="off"
            :placeholder="$t('propTrade.resetPassword.confirmNewPwdPlaceholder')"
            v-model="resetPwdForm.confirmNewPwd"
            dataTestId="confirmNewPwd"
          />
        </el-form-item>
      </el-form>

      <div class="action-area">
        <el-button class="blue_button change-pwd-button" type="primary" data-testId="changePassword" @click="changePassword">
          <div class="action-button-text">{{ $t('propTrade.button.changePwd') }}</div>
        </el-button>

        <div class="forgot-pwd-button" @click="forgetPwdAndSendEmail">
          {{ $t('propTrade.resetPassword.forgotPwd') }}
        </div>
      </div>
    </el-dialog>

    <!-- payout dialog -->
    <el-dialog :visible.sync="showPayoutDialog" class="payout-dialog" top="0">
      <div class="reset-pwd-body">
        <div class="title-text">
          {{ $t('propTrade.payoutPage.howToProcessYourProfitSplit') }}
        </div>
        <div class="desc-text">
          {{ $t('propTrade.payoutPage.howToProcessYourProfitSplitDesc') }}
        </div>
        <div class="desc-text">
          {{ $t('propTrade.payoutPage.howToProcessYourProfitSplitDesc2') }}
        </div>
        <div class="desc-text">
          {{ $t('propTrade.payoutPage.howToProcessYourProfitSplitDesc3') }}
        </div>
        <div class="desc-text">
          {{ $t('propTrade.payoutPage.howToProcessYourProfitSplitDesc4') }}
        </div>
        <div class="desc-text">
          {{ $t('propTrade.payoutPage.howToProcessYourProfitSplitDesc5') }}
        </div>
      </div>

      <div class="action-area">
        <el-button class="white_button" data-testId="cancelPayoutDialog" @click="showPayoutDialog = false">
          <div class="action-button-text">
            {{ $t('propTrade.button.cancel') }}
          </div>
        </el-button>
        <el-button class="blue_button" type="primary" data-testId="gotoPayout" @click="gotoPayout">
          <div class="action-button-text">
            {{ $t('propTrade.payoutPage.submit') }}
          </div>
        </el-button>
      </div>
    </el-dialog>

    <!-- information dialogs - start -->
    <!-- reset pwd dialog -->
    <el-dialog :visible.sync="showInfoDialog" class="reset-pwd-info-dialog" top="0">
      <div class="info-head">
        <img class="info-head-icon" :src="require(`@/assets/images/propTrade/reset-pwd-info.png`)" alt="resetPwdInfo" />
        <div class="info-head-title">{{ $t('propTrade.button.resetPassword') }}</div>
      </div>
      <div class="info-container">
        <div>{{ infoDialogMessage }}</div>
      </div>
    </el-dialog>

    <!-- poa dialog -->
    <el-dialog :visible.sync="showPoaDialog"  class="poa-info-dialog" top="0">
      <div class="info-head">
        <img class="info-head-icon alert" :src="require(`@/assets/images/propTrade/dialog_fail.png`)" alt="resetPwdInfo" />
        <div class="info-head-title">
          {{ $t('propTrade.payoutPage.poaDialog.title') }}
        </div>
      </div>

      <div class="info-container">
        <div>{{ $t('propTrade.payoutPage.poaDialog.message') }}</div>
      </div>

      <div class="action-area">
        <el-button class="white_button" data-testId="cancelVerifyPoa" @click="showPoaDialog = false">
          <div class="action-button-text">
            {{ $t('propTrade.button.cancel') }}
          </div>
        </el-button>
        <el-button class="blue_button" type="primary" data-testId="verifyPoa" @click="verifyNow">
          <div class="action-button-text">
            {{ $t('propTrade.button.verifyNow') }}
          </div>
        </el-button>
      </div>
    </el-dialog>
    <!-- information dialogs - end -->
  </div>
</template>

<script>
import AccountIcon from '@/components/propTrading/components/dashboard/AccountIcon.vue';
import EquityNumber from '@/components/propTrading/components/dashboard/EquityNumber.vue';
import AccountStatus from '@/components/propTrading/components/dashboard/AccountStatus.vue';
import auditStatus from '@/constants/auditStatus';
import { SESSION_STATUS_REV, ACCOUNT_STATUS_REV, ACCOUNT_STATUS, ACCOUNT_STAGE } from '@/constants/propTrade';
import { rsa } from '@/util/encrypt';
import { apiResetPassword, apiGetSessionHistory, apiForgetPassword } from '@/resource/propTrading';
import { mapState, mapGetters } from 'vuex';
import { currencyToSymbol } from '@/util/filter';

import settingIcon from '@/assets/images/propTrade/setting.png';
import hoverSettingIcon from '@/assets/images/propTrade/setting_hover.png';
import lockIcon from '@/assets/images/propTrade/lock.png';
import lockHoverIcon from '@/assets/images/propTrade/lock_hover.png';
import payoutIcon from '@/assets/images/propTrade/payout.png';
import payoutHoverIcon from '@/assets/images/propTrade/payout_hover.png';
import historyIcon from '@/assets/images/propTrade/history.png';
import historyHoverIcon from '@/assets/images/propTrade/history_hover.png';

export default {
  name: 'DashboardAccount',
  props: {
    newAccount: {
      type: Boolean,
      default: false
    },
    accountData: {
      type: Object,
      default: () => ({})
    },
    containerWidth: {
      type: Number | String,
      default: 0
    }
  },
  components: {
    AccountIcon,
    EquityNumber,
    AccountStatus
  },
  data() {
    return {
      ACCOUNT_STATUS,
      ACCOUNT_STAGE,
      ACCOUNT_STATUS_REV,
      SESSION_STATUS_REV,
      showRestPassword: false,
      showPayoutDialog: false,
      showInfoDialog: false,
      showPoaDialog: false,
      showHistory: false,
      historyData: [],
      resetPwdForm: {
        currentPwd: '',
        newPwd: '',
        confirmNewPwd: ''
      },
      infoDialogMessage: '',
      accountSettingIcon: settingIcon,
      hoverdSettingItem: null
    };
  },
  computed: {
    ...mapState('common', ['poiAuditStatus', 'poaAuditStatus']),
    ...mapGetters('propTrading', ['accountStageMap']),
    lang() {
      return this.$store.state.common.lang;
    },
    calFailedClass() {
      return this.accountData?.status === ACCOUNT_STATUS.FAILED ? 'failed' : '';
    },
    calNewAccount() {
      return this.newAccount;
    },
    calAccountData() {
      return { ...this.accountData };
    },
    hasPayout() {
      return !!this.accountData.payout;
    },
    dropdownList() {
      const defaultMenu = [
        {
          title: this.$t('propTrade.button.resetPassword'),
          value: 'resetPassword',
          icon: lockIcon,
          hoverIcon: lockHoverIcon
        },
        this.accountData.payout
          ? {
              title: this.$t('propTrade.button.payout'),
              value: 'payout',
              icon: payoutIcon,
              hoverIcon: payoutHoverIcon
            }
          : null,
        {
          title: this.$t('propTrade.button.history'),
          value: 'history',
          icon: historyIcon,
          hoverIcon: historyHoverIcon
        }
      ];

      return defaultMenu.filter(Boolean);
    },
    settingIcons() {
      return { defaultIcon: settingIcon, hoverIcon: hoverSettingIcon };
    },
    drawerWidth() {
      if (this.containerWidth <= 768) {
        return '100%';
      } else {
        return '50%';
      }
    },
    drawerColumnWidth() {
      if (this.containerWidth <= 768) {
        return '120';
      } else {
        return '240';
      }
    },
    resetFormRules() {
      return {
        currentPwd: [
          {
            required: true,
            message: this.$t('common.formValidation.dynamicReq', {
              dynamic: this.$t('propTrade.resetPassword.currentPwd')
            }),
            trigger: 'blur'
          }
        ],
        newPwd: [
          {
            required: true,
            message: this.$t('common.formValidation.dynamicReq', {
              dynamic: this.$t('propTrade.resetPassword.newPwd')
            }),
            trigger: 'blur'
          },
          {
            validator: (rule, value, callback) => {
              if (this.resetPwdForm?.currentPwd && value === this.resetPwdForm.currentPwd) {
                callback(new Error(this.$t('propTrade.resetPassword.newPwdErrorMsg')));
              }
              callback();
            },
            trigger: 'blur'
          },
          {
            validator: (rule, value, callback) => {
              const regEpx = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,16}$/;
              if (!regEpx.test(value)) {
                callback(new Error(this.$t('common.formValidation.newPwFormat')));
              }
              callback();
            },
            trigger: 'blur'
          }
        ],
        confirmNewPwd: [
          {
            required: true,
            message: this.$t('common.formValidation.dynamicReq', {
              dynamic: this.$t('propTrade.resetPassword.confirmNewPwd')
            }),
            trigger: 'blur'
          },
          {
            validator: (rule, value, callback) => {
              if (this.resetPwdForm?.newPwd && value !== this.resetPwdForm.newPwd) {
                callback(new Error(this.$t('propTrade.resetPassword.confirmNewPwdErrorMsg')));
              }
              callback();
            },
            trigger: 'blur'
          }
        ]
      };
    },
    statusDesc() {
      if (this.hasPayout) {
        return `${this.$t('propTrade.button.payout')} : `;
      }

      if (this.accountData.statusDate) {
        return `${this.$t('propTrade.end')} : `;
      }

      return '';
    },
    isStatusDescVisible() {
      const isEligibleAccountStatus = [
        ACCOUNT_STATUS.ACTIVE,
        ACCOUNT_STATUS.FAILED,
        ACCOUNT_STATUS.PASSED,
        ACCOUNT_STATUS.VERIFYING
      ].includes(this.accountData.status);
      return isEligibleAccountStatus ? this.hasPayout || this.accountData.statusDate : false;
    },
    isAuthVisible() {
      return (
        [ACCOUNT_STAGE.EVALUATION, ACCOUNT_STAGE.EVALUATION_PLUS].includes(this.accountData.accountStage) &&
        this.accountData.status === ACCOUNT_STATUS.VERIFYING &&
        this.poiAuditStatus !== auditStatus.completed
      );
    },
    calIsFailAccount() {
      return this.calAccountData.status == this.ACCOUNT_STATUS.FAILED;
    }
  },
  watch: {
  showRestPassword(val) {
    this.$store.commit('propTrading/SET_IS_REFETCH', !val);
  },
  showPayoutDialog(val) {
    this.$store.commit('propTrading/SET_IS_REFETCH', !val);
  },
  showInfoDialog(val) {
    this.$store.commit('propTrading/SET_IS_REFETCH', !val);
  },
  showPoaDialog(val) {
    this.$store.commit('propTrading/SET_IS_REFETCH', !val);
  },
  showHistory(val) {
    this.$store.commit('propTrading/SET_IS_REFETCH', !val);
  }
},
  // watch: {
  //   showRestPassword: {
  //     immediate: true,
  //     handler(newValue) {
  //       this.handleZendeskWidgetMinimized(newValue);
  //     }
  //   },
  //   showInfoDialog: {
  //     immediate: true,
  //     handler(newValue) {
  //       this.handleZendeskWidgetMinimized(newValue);
  //     }
  //   }
  // },
  methods: {
    handleCommand(item) {
      switch (item) {
        case 'history':
          this.getSessionHistory(this.accountData.sessionId);
          return;
        case 'resetPassword':
          this.showRestPassword = true;
          // zE('webWidget', 'hide');
          return;
        case 'payout':
          if (this.poaAuditStatus !== auditStatus.completed) {
            this.showPoaDialog = true;
          } else {
            this.showPayoutDialog = true;
          }
          return;
        default:
          return;
      }
    },
    async gotoPayout() {
      await this.$router
        .push({
          name: 'propTradingPayout',
          params: { accountNumber: encodeURIComponent(this.accountData.accountNumber) }
        })
        .catch(() => {});
    },
    async verifyNow() {
      await this.$router.push({ name: 'register' }).catch(() => {});
    },
    async showAccountMetrics() {
      await this.$router
        .push({
          name: 'propTradingMetrics',
          params: { accountNumber: encodeURIComponent(this.accountData.accountNumber) }
        })
        .catch(() => {});
    },
    async getSessionHistory(sessionId) {
      this.historyData = [];
      const result = await apiGetSessionHistory(sessionId);

      if (result.data.code === 0) {
        this.historyData = result.data.data
          .sort((a, b) => b.id - a.id)
          .map(history => {
            const { accountStage, amount, currency, mt4Account } = history?.vars || {};
            let event = null;
            switch (history?.type) {
              case 0:
              case 1:
              case 7:
              case 9:
              case 10:
              case 11:
                event = this.$t(`propTrade.history.historyEvent.${history.type}`);
                break;
              case 2:
              case 3:
              case 4:
              case 5:
                let accountStageText = this.$t(`propTrade.accountStageContext.${this.accountStageMap[accountStage]}`);
                event = this.$t(`propTrade.history.historyEvent.${history.type}`, {
                  accountStage: accountStageText,
                  accountNumber: mt4Account
                });
                break;
              case 6:
              case 8:
                event = this.$t(`propTrade.history.historyEvent.${history.type}`, {
                  currency: currencyToSymbol(currency),
                  amount
                });
                break;
              default:
                event = history?.event;
                break;
            }

            return {
              date: history.displayDate,
              event
            };
          });
      }

      this.showHistory = true;
    },
    // getHistoryStatusContext(type) {
    //   switch (type) {
    //     case 0:
    //       return this.$t('propTrade.history.status.refunded');
    //     case 1:
    //       return this.$t('propTrade.history.status.liveAccountCreated', {
    //         account: '777777',
    //       });
    //     case 2:
    //       return this.$t('propTrade.history.status.verifyAccount', { account: '777777' });
    //     case 3:
    //       return this.$t('propTrade.history.status.challangeAccountPassed', {
    //         account: '777777',
    //       });
    //     case 4:
    //       return this.$t('propTrade.history.status.challangeAccountCreated', {
    //         account: '777777',
    //       });
    //     case 5:
    //       return this.$t('propTrade.history.status.paymentSuccess');
    //     default:
    //       return '-';
    //   }
    // },
    cancelChangePassword() {
      this.$refs.resetPwdFormRef.resetFields();
      this.showRestPassword = false;
      // zE('webWidget', 'show');
    },
    changePassword() {
      this.$refs.resetPwdFormRef.validate(async valid => {
        if (valid) {
          const result = await apiResetPassword({
            accountNumber: this.accountData.accountNumber,
            currentPassword: rsa(this.resetPwdForm.currentPwd),
            newPassword: rsa(this.resetPwdForm.newPwd),
            confirmNewPassword: rsa(this.resetPwdForm.confirmNewPwd)
          });

          if (result.data.code === 0) {
            this.$refs.resetPwdFormRef.resetFields();
            this.infoDialogMessage = this.$t('propTrade.resetPassword.succ2');
            this.showInfoDialog = true;
            this.showRestPassword = false;
          }
        }
      });
    },
    // startChat() {
    //   zE('webWidget', 'open');
    //   zE('webWidget', 'show');
    // },
    closeResetInfoDialog() {
      this.showInfoDialog = false;
      // zE('webWidget', 'show');
    },
    // handleZendeskWidgetMinimized(target) {
    //   if (target) {
    //     zE('webWidget', 'hide');
    //   } else {
    //     zE('webWidget', 'show');
    //   }
    // },
    async forgetPwdAndSendEmail() {
      try {
        const baseUrl = location.protocol + '//' + location.hostname;
        const res = await apiForgetPassword({
          mtAccount: this.accountData.mt4Account,
          baseUrl,
          accountNumber: this.accountData.accountNumber
        });
        if (res.data.code == 0 && res.data.data) {
          this.infoDialogMessage = this.$t('propTrade.resetPassword.succ');
          this.showRestPassword = false;
          this.showInfoDialog = true;
        } else {
          this.$message({
            message: this.$t('home.pw.error'),
            type: 'error'
          });
        }
      } catch (error) {
        this.$message({
          message: this.$t('home.pw.error'),
          type: 'error'
        });
      }
    },
    selectSettingItem(item) {
      this.hoverdSettingItem = item;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/pages/propTrading/components/dashboard/account-or-payment-status.scss';
/deep/ .el-dropdown-menu__item:not(.is-disabled):hover,
.el-select-dropdown__item.selected,
.el-select-dropdown__item.hover,
.el-select-dropdown__item:hover {
  color: #006bff;
}
/deep/ .el-table--striped {
  .el-table__body {
    tr.el-table__row--striped td {
      background: #fff;
    }
  }
}
</style>
